import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { userPreferencesApi } from '@/api/userPreferencesApi'
import {
  DEFAULT_HOME_LAYOUT_PREFS,
  activeHomeLayout,
  addHomeLayout,
  coerceHomeLayoutPrefs,
  deleteHomeLayout,
  homeLayoutsKey,
  isDefaultHomeLayoutPrefs,
  saveHomeLayoutTiles,
  selectHomeLayout,
  type HomeLayoutPrefs,
} from '@/lib/homeLayouts'
import { useAuth } from '@/providers/AuthProvider'
import { TABLE_PREFS_QUERY_KEY } from '@/providers/TableColumnPrefsProvider'
import { useTenant } from '@/providers/TenantProvider'

const WRITE_DEBOUNCE_MS = 600

function newLayoutId(): string {
  return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`
}

/** A value and the key it belongs to, so it can never be read or saved under another tenant's key. */
type Keyed = { key: string; prefs: HomeLayoutPrefs }

/**
 * The signed-in user's saved Home layouts in the selected tenant (z8nrz7ep6x).
 *
 * Same storage path as `useBoardPrefs`: the shared `GET /preferences` map (no
 * extra request on Home), changes applied at once, saved after a short quiet
 * period, flushed with `keepalive` when the page goes away, and not rolled back
 * on failure. The default (no layouts, none active) is deleted, not stored.
 *
 * Each write replaces the whole value, so two open tabs edit last-writer-wins:
 * a layout made in one tab is lost if an older tab writes after it.
 */
export function useHomeLayouts() {
  const { isAuthenticated } = useAuth()
  const { selectedTenantId } = useTenant()
  const key = selectedTenantId ? homeLayoutsKey(selectedTenantId) : null
  const queryClient = useQueryClient()
  const { data: serverMap, isLoading } = useQuery({
    queryKey: TABLE_PREFS_QUERY_KEY,
    queryFn: userPreferencesApi.getAll,
    enabled: isAuthenticated,
    staleTime: Infinity,
    retry: 1,
  })

  const [local, setLocal] = useState<Keyed | null>(null)
  const localPrefs = local && local.key === key ? local.prefs : null
  const prefs = useMemo(
    () =>
      localPrefs ??
      (serverMap && key ? coerceHomeLayoutPrefs(serverMap[key]) : DEFAULT_HOME_LAYOUT_PREFS),
    [localPrefs, serverMap, key],
  )

  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Carries its key: a save still waiting when the tenant changes goes to the
  // tenant it was made in, not the one now selected.
  const pending = useRef<Keyed | null>(null)

  const flush = useCallback(
    (unloading = false) => {
      if (timer.current) {
        clearTimeout(timer.current)
        timer.current = null
      }
      const next = pending.current
      if (!next) return
      pending.current = null
      const opts = unloading ? { keepalive: true } : undefined
      const keep = !isDefaultHomeLayoutPrefs(next.prefs)
      const save = keep
        ? userPreferencesApi.set(next.key, next.prefs, opts)
        : userPreferencesApi.remove(next.key, opts)
      void save
        .then(() => {
          queryClient.setQueryData<Record<string, unknown>>(TABLE_PREFS_QUERY_KEY, prev => {
            const map = { ...(prev ?? {}) }
            if (keep) map[next.key] = next.prefs
            else delete map[next.key]
            return map
          })
        })
        .catch(() => {})
    },
    [queryClient],
  )

  const update = useCallback(
    (next: HomeLayoutPrefs) => {
      if (!key) return
      // A save for another tenant still waiting goes out now, under its own key.
      if (pending.current && pending.current.key !== key) flush()
      setLocal({ key, prefs: next })
      pending.current = { key, prefs: next }
      if (timer.current) clearTimeout(timer.current)
      timer.current = setTimeout(() => flush(), WRITE_DEBOUNCE_MS)
    },
    [flush, key],
  )

  useEffect(() => {
    const onPageHide = () => flush(true)
    const onHidden = () => {
      if (document.visibilityState === 'hidden') flush(true)
    }
    window.addEventListener('pagehide', onPageHide)
    document.addEventListener('visibilitychange', onHidden)
    return () => {
      window.removeEventListener('pagehide', onPageHide)
      document.removeEventListener('visibilitychange', onHidden)
      flush(false)
    }
  }, [flush])

  return {
    prefs,
    /** Until the stored layouts arrive, Home shows nothing rather than flashing the default. */
    isLoading: isAuthenticated && isLoading && !localPrefs,
    /**
     * Whether the stored layouts are known and have a tenant to belong to.
     * Every write replaces the whole value, so writing before the read lands
     * (or after it failed) would wipe the layouts the user already has.
     */
    ready: key !== null && (!!localPrefs || !!serverMap),
    active: activeHomeLayout(prefs),
    select: (id: string | null) => update(selectHomeLayout(prefs, id)),
    create: (name: string, tiles: string[]) =>
      update(addHomeLayout(prefs, { id: newLayoutId(), name: name.trim(), tiles })),
    saveTiles: (id: string, tiles: string[]) => update(saveHomeLayoutTiles(prefs, id, tiles)),
    remove: (id: string) => update(deleteHomeLayout(prefs, id)),
  }
}
